import React from "react";
import { useIntl } from "react-intl";
import { Member, UpdateOwner, MemberPageParam } from "@/model";
import { UTCToLocalString } from "@/util/dateUtil";
import { hasAdminPermission } from "@/util/permissionUtil";
import { useRole } from "@/query/role";
import { usePermissions } from "@/query/permission";
import { useUpdateOwner, useDeleteMember } from "@/query/member";
import { useAgentId, useDialog, useModal } from "@/hook";
import { Popover, PopoverButton, PopoverItem, PopoverMenu } from "@/component/Popover";
import { errorToast, successToast } from "@/component/Toast";
import HighlightText from "@/component/HighlightText";
import SortButton from "@/component/SortBurtton";
import { DeleteIcon, EditIcon } from "@/assets/svgs/icons";

import { MEMBER_MODIFY_MODAL_ID } from "./MemberModifyModal";


interface MemberTableProps {
    keyword?: string;
    members: Member[];
    sortKey: MemberPageParam["sortKey"];
    sortOrder: MemberPageParam["order"];
    onSort: (key: MemberPageParam["sortKey"]) => void;
}

const MemberTable: React.FC<MemberTableProps>  = (props) => {
    const { keyword, members, sortKey, sortOrder, onSort } = props;

    const intl = useIntl();
    const agentId = useAgentId();
    const modal = useModal();
    const dialog = useDialog();

    const { data: userPermissions } = usePermissions(agentId);
    const { data: roles } = useRole(agentId);
    const { mutateAsync: deleteMember } = useDeleteMember(agentId);
    const { mutateAsync: updateOwner } = useUpdateOwner(agentId);

    const onClickDelete = (member: Member) => {
        dialog.open({
            title: intl.formatMessage({ id: "i12018" }),
            content: intl.formatMessage({ id: "i12019" }),
            variant: "danger",
            onConfirm: async () => {
                await deleteMember(member._id)
                    .then(() => {
                        successToast(intl.formatMessage({ id: "t005" }));
                    })
                    .catch(() => {
                        errorToast(intl.formatMessage({ id: "t006" }));
                    });
            }
        });
    };

    const onClickUpdateOwner = (newOwner: Member) => {
        const owner = members.find(member => member.is_admin);
        if (!owner){
            return;
        }
        const username = newOwner.user.username;
        const form: UpdateOwner = {
            owner_member_id:owner._id,
            new_owner_member_id: newOwner._id,
            role_id: roles![0].id,
            management_unit_role_id: undefined
        };

        dialog.open({
            title: intl.formatMessage({ id: "i12027" }),
            content: (intl.formatMessage({ id: "i12028" }, { username })),
            variant: "primary",
            size: "md",
            onConfirm: async () => {
                await updateOwner(form)
                    .then(() => {
                        successToast(intl.formatMessage({ id: "t003" }));
                    })
                    .catch(() => {
                        errorToast(intl.formatMessage({ id: "t004" }));
                    });
            }
        });
    };

    return (<>
        <div role="table"
             className="selection-table grid-table-member"
        >
            <div role="rowgroup"
                 className="selection-table-thead"
            >
                <div role="columnheader"
                     className="selection-table-th pl-[25px]"
                >
                    {intl.formatMessage({ id: "i12005" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[25px]"
                >
                    {intl.formatMessage({ id: "i12004" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th group pl-[25px]"
                >
                    <SortButton order={sortOrder}
                                active={sortKey === "department"}    
                                onClick={() => onSort("department")}
                    >
                        {intl.formatMessage({ id: "i12006" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     className="selection-table-th group pl-[25px]"
                >
                    <SortButton order={sortOrder}
                                active={sortKey === "member_type"}    
                                onClick={() => onSort("member_type")}
                    >
                        {intl.formatMessage({ id: "i12007" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[25px]"
                >
                    {intl.formatMessage({ id: "i12008" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th"
                />
            </div>
            <div role="rowgroup"
                 className="selection-table-tbody"
            >
                {members.map(member => (
                    <div key={member.user_id}
                         role="row"
                         className="selection-table-tr group"
                    >
                        <div role="cell"
                             className="selection-table-td pl-[25px]"
                        >
                            <HighlightText query={member.user.username}
                                            keyword={keyword}
                                            highlightClassName="text-highlight"
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td pl-[25px]"
                        >
                            <HighlightText query={member.user.nickname}
                                           keyword={keyword}
                                           highlightClassName="text-highlight"
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td pl-[25px]"
                        >
                            <HighlightText query={member.user.department || "-"}
                                           keyword={keyword}
                                           highlightClassName="text-highlight"
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td pl-[25px]"
                        >
                            {member.is_admin
                                ? intl.formatMessage({ id: "i12022" })
                                : member.roles.map(role => role.description).join(", ")
                            }
                        </div>
                        <div role="cell"
                             className="selection-table-td pl-[25px]"
                        >
                            {member.updated_at
                                ? UTCToLocalString(member.updated_at, "YYYY-MM-DD HH:mm")
                                : "-"
                            }
                        </div>
                        <div role="cell"
                             className="selection-table-td"
                        >
                            {!member.is_admin && (
                                <Popover>
                                    {({ open, close }) => (
                                        <>
                                            <PopoverButton className="selection-table-popover-btn"
                                                           open={open}
                                                           close={close}
                                            />
                                            <PopoverMenu>
                                                <PopoverItem className="selection-table-popover-item"
                                                            onClick={() => modal.open(MEMBER_MODIFY_MODAL_ID, member)}
                                                >
                                                    <EditIcon className="size-3.5"/>
                                                    {intl.formatMessage({ id: "i12020" })}
                                                </PopoverItem>
                                                <PopoverItem className="selection-table-popover-item text-point-red"
                                                            onClick={() => onClickDelete(member)}
                                                >
                                                    <DeleteIcon className="size-3.5"/>
                                                    {intl.formatMessage({ id: "i12018" })}
                                                </PopoverItem>
                                                {userPermissions && hasAdminPermission(userPermissions) && (
                                                    <PopoverItem className="selection-table-popover-item"
                                                                onClick={() => onClickUpdateOwner(member)}
                                                    >
                                                        <EditIcon className="size-3.5"/>
                                                        {intl.formatMessage({ id: "i12027" })}
                                                    </PopoverItem>
                                                )}
                                            </PopoverMenu>
                                        </>
                                    )}
                                </Popover>
                            )}
                        </div>
                    </div>
                ))}
                {members.length === 0 && (
                    <div className="empty-table">
                        {intl.formatMessage({ id: keyword ? "b0014" : "b0018" })}
                    </div>
                )}
            </div>
        </div>
    </>);
};

export default MemberTable;